import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import isNil from 'lodash/isNil';

import Spinner from '../Spinner';
import Heading from '../Heading';
import { Box, Stack } from '../LayoutPrimitives';
import Text from '../Text';

import Modal from './Modal';

export default function SpinnerModal({
    children,
    heading = 'Please wait ...',
    messages = [],
    messageInterval = 7000,
    isOpen = false,
    isSpinning = true,
    ...rest
}) {
    const timeout = useRef();
    const interval = useRef();
    const [messageIndex, setMessageIndex] = useState();

    useEffect(() => {
        let isMounted = true;
        if (isOpen && messages && messages.length > 0) {
            timeout.current = setTimeout(() => {
                if (isMounted) {
                    setMessageIndex(0);
                }

                interval.current = setInterval(() => {
                    if (isMounted) {
                        setMessageIndex((prev) => {
                            if (prev === messages.length - 1) {
                                return 0;
                            }
                            return prev + 1;
                        });
                    }
                }, messageInterval);
            }, 3000);
        }

        return () => {
            isMounted = false;
            clearTimeout(timeout.current);
            clearInterval(interval.current);
        };
    }, [isOpen, messages, messageInterval]);

    return (
        <Modal
            aria-label="Please wait"
            size="sm"
            shouldShowCloseButton={false}
            isOpen={isOpen}
            {...rest}
        >
            {isSpinning && (
                <Stack>
                    <Spinner size="md" />
                    <Heading
                        headingLevel={1}
                        appearance="h5"
                        style={{ textAlign: 'center' }}
                    >
                        {heading}
                    </Heading>
                    {!isNil(messageIndex) && (
                        <Box paddingX="lg">
                            <Text style={{ textAlign: 'center' }} aria-live="polite">
                                {messages[messageIndex]}
                            </Text>
                        </Box>
                    )}
                </Stack>
            )}

            {!isSpinning && <div aria-live="polite">{children}</div>}
        </Modal>
    );
}

SpinnerModal.propTypes = {
    /** If the modal is open or not */
    isOpen: PropTypes.bool,

    /** If the spinner and rotating messages are visible */
    isSpinning: PropTypes.bool,

    /** Heading displayed while spinning */
    heading: PropTypes.node,

    /** Array of messages to display while spinning. */
    messages: PropTypes.array,

    /** The length of time each message is visible before the next message in the array is shown */
    messageInterval: PropTypes.number,

    /** The content shown if isSpinning is false */
    children: PropTypes.node,
};
